export class LoopPlayer {
  private current: { id: string; stop: () => void; gain: GainNode } | null = null;

  constructor(private readonly ctx: AudioContext) {}

  public getCurrentLoopId(): string | null {
    return this.current?.id ?? null;
  }

  public crossfadeTo(params: {
    id: string;
    startLoop: () => { stop: () => void; gainNode: GainNode } | null;
    fadeMs: number;
    targetGain?: number;
  }): void {
    if (this.current?.id === params.id) {
      return;
    }

    const now = this.ctx.currentTime;
    const fade = Math.max(0.2, params.fadeMs / 1000);

    const target =
      typeof params.targetGain === "number" &&
      Number.isFinite(params.targetGain) &&
      params.targetGain >= 0
        ? params.targetGain
        : 1;

    const next = params.startLoop();
    if (!next) return;

    next.gainNode.gain.setValueAtTime(0, now);
    next.gainNode.gain.linearRampToValueAtTime(target, now + fade);

    const prev = this.current;
    this.current = { id: params.id, stop: next.stop, gain: next.gainNode };

    if (prev) this.fadeOutAndStop(prev.gain, prev.stop, now, fade);
  }

  public stopAll(fadeMs = 200): void {
    if (!this.current) return;

    const now = this.ctx.currentTime;
    const fade = Math.max(0.1, fadeMs / 1000);
    this.fadeOutAndStop(this.current.gain, this.current.stop, now, fade);
    this.current = null;
  }

  /**
   * Fades a loop out from the level it is at now and stops it once that fade has run. Its own
   * pending automation is dropped first: a loop still ramping in kept rising toward full under the
   * fade-out, a swell just as it should go (review finding 52). The stop waits for the fade's real
   * length, floor included: timed from the requested fade, a short one cut the loop mid-ramp (29).
   */
  private fadeOutAndStop(gainNode: GainNode, stop: () => void, now: number, fadeSeconds: number): void {
    const level = gainNode.gain.value;
    gainNode.gain.cancelScheduledValues(now);
    gainNode.gain.setValueAtTime(level, now);
    gainNode.gain.linearRampToValueAtTime(0, now + fadeSeconds);
    setTimeout(stop, fadeSeconds * 1000 + 50);
  }

  // Synchronous variant of stopAll. Cancels any in-flight gain ramp, zeros
  // the bus, and calls the underlying BufferSource's stop() inline — no
  // setTimeout. Required for browser-lifecycle teardown (pagehide → BFCache
  // on iOS Safari) where a deferred stop may never run before the page is
  // frozen, causing the loop to resume audibly on restore.
  public stopAllImmediate(): void {
    if (!this.current) return;

    const cur = this.current;
    this.current = null;

    try {
      cur.gain.gain.cancelScheduledValues(this.ctx.currentTime);
      cur.gain.gain.value = 0;
    } catch {}

    cur.stop();
  }
}